import { useRef, useState, type DragEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  CheckCircle2,
  FileSpreadsheet,
  Trash2,
  Upload,
  X,
} from 'lucide-react'
import {
  ENTITY_LABELS,
  FILE_KIND_LABELS,
  groupChanges,
  hasWork,
  missingKinds,
  refreshRegistrationLinks,
  rejectReason,
  runImport,
  fetchLastImport,
  LAST_IMPORT_KEY,
  totalOf,
  unaccounted,
  accountParts,
  type ImportPlan,
  type RowAccount,
} from '../api/imports'
import { formatFullDate } from '../api/calendar'
import ImportGuide from './ImportGuide'
import FilingConflicts from './FilingConflicts'

/** A file the browser is holding, with any reason it cannot be sent. */
type Staged = { file: File; problem: string | null }

/**
 * Bring Jackrabbit's exports across.
 *
 * Jackrabbit stays the system of record while this is built (D-54), so the
 * studio's data drifts here daily. This is how it catches up: drop the exports
 * in, read what would change, then apply it.
 *
 * **Preview is not optional.** The apply button does not exist until a preview
 * has been run and returned, because an import that writes before anyone has
 * seen what it intends to do cannot be understood by reading the result — 1,300
 * rows landed correctly and 1,300 rows landed wrongly look the same afterwards.
 */
export default function ImportView() {
  const [staged, setStaged] = useState<Staged[]>([])
  const [plan, setPlan] = useState<ImportPlan | null>(null)
  const [dragging, setDragging] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()

  const sendable = staged.filter((s) => s.problem === null).map((s) => s.file)

  const add = (incoming: FileList | File[]) => {
    const files = Array.from(incoming)

    setStaged((current) => {
      // Same file dropped twice is a slip, not an instruction to import it
      // twice. Matched on name and size, which is as close as the browser gets.
      const seen = new Set(current.map((s) => `${s.file.name}:${s.file.size}`))

      const additions = files
        .filter((f) => !seen.has(`${f.name}:${f.size}`))
        .map((file) => ({ file, problem: rejectReason(file) }))

      return [...current, ...additions]
    })

    // A new set of files invalidates the plan describing the old one.
    setPlan(null)
  }

  const remove = (index: number) => {
    setStaged((current) => current.filter((_, i) => i !== index))
    setPlan(null)
  }

  const reset = () => {
    setStaged([])
    setPlan(null)
    preview.reset()
    apply.reset()
  }

  const preview = useMutation({
    mutationFn: () => runImport(sendable, false),
    onSuccess: setPlan,
  })

  const apply = useMutation({
    mutationFn: () => runImport(sendable, true),
    onSuccess: (result) => {
      setPlan(result)
      // Classes, rosters and the calendar have all potentially just moved.
      queryClient.invalidateQueries()
    },
  })

  const busy = preview.isPending || apply.isPending
  const error = (preview.error ?? apply.error) as Error | null

  const onDrop = (event: DragEvent) => {
    event.preventDefault()
    setDragging(false)
    if (event.dataTransfer.files.length > 0) add(event.dataTransfer.files)
  }

  return (
    <div className="page-shell px-5 py-8">
      <header className="mb-5">
        <p className="text-[11px] font-bold tracking-[0.14em] text-accent uppercase">Admin</p>
        <h1 className="font-display text-2xl">Import from Jackrabbit</h1>
        <p className="mt-1.5 max-w-[44rem] text-[13.5px] text-ink-3">
          Run the five exports in Jackrabbit, drop the files here, and check what would
          change before applying it. Only differences are written &mdash; re-importing the
          same files does nothing, so this is safe to run as often as you like.
        </p>

        <LastImported />
      </header>

      <ImportGuide />

      {/* ---- Drop zone ---- */}
      <div
        onDragOver={(e) => {
          e.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`rounded-2xl border-2 border-dashed px-5 py-9 text-center transition-colors ${
          dragging ? 'border-accent bg-accent-soft' : 'border-line-2 bg-surface'
        }`}
      >
        <div className="mx-auto grid size-11 place-items-center rounded-xl bg-surface-2 text-ink-3">
          <Upload size={19} strokeWidth={2} />
        </div>

        <p className="mt-3 text-[14px] font-semibold">Drop the exports here</p>
        <p className="mt-1 text-[12.5px] text-ink-3">
          Families, students, staff, classes and enrolment detail
        </p>

        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="mt-3.5 rounded-lg border border-line-2 bg-surface-2 px-3.5 py-1.5 text-[13px] font-semibold hover:border-accent hover:text-accent-2"
        >
          Choose files
        </button>

        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".xls"
          className="hidden"
          onChange={(e) => {
            if (e.target.files) add(e.target.files)
            // Cleared so choosing the same file again still fires a change.
            e.target.value = ''
          }}
        />
      </div>

      {/* ---- Staged files ---- */}
      {staged.length > 0 && (
        <section className="mt-5 overflow-hidden rounded-2xl border border-line bg-surface">
          <header className="flex items-center gap-2 border-b border-line bg-surface-2 px-4 py-2.5">
            <h2 className="text-[13px] font-bold">
              Ready to send
              <span className="tabular ml-2 font-semibold text-ink-3">{sendable.length}</span>
            </h2>
            <button
              type="button"
              onClick={reset}
              className="ml-auto text-[12.5px] font-semibold text-ink-3 hover:text-ink"
            >
              Clear all
            </button>
          </header>

          <ul className="divide-y divide-line">
            {staged.map((item, i) => (
              <li key={`${item.file.name}-${i}`} className="flex items-center gap-3 px-4 py-2.5">
                <FileSpreadsheet
                  size={15}
                  strokeWidth={2}
                  className={`shrink-0 ${item.problem ? 'text-crit' : 'text-ink-3'}`}
                />

                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-semibold">{item.file.name}</p>
                  {item.problem ? (
                    <p className="text-[12px] text-crit">{item.problem}</p>
                  ) : (
                    <p className="tabular text-[12px] text-ink-3">
                      {Math.max(1, Math.round(item.file.size / 1024))} KB
                    </p>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => remove(i)}
                  aria-label={`Remove ${item.file.name}`}
                  className="grid size-7 shrink-0 place-items-center rounded-lg text-ink-3 hover:bg-surface-2 hover:text-ink"
                >
                  <X size={14} strokeWidth={2.5} />
                </button>
              </li>
            ))}
          </ul>

          <footer className="flex flex-wrap items-center gap-2.5 border-t border-line bg-surface-2 px-4 py-3">
            <button
              type="button"
              onClick={() => preview.mutate()}
              disabled={busy || sendable.length === 0}
              className="rounded-lg bg-accent px-4 py-2 text-[13px] font-semibold text-on-accent hover:brightness-110 disabled:opacity-50"
            >
              {preview.isPending ? 'Reading the files...' : 'Check what would change'}
            </button>

            {plan && !plan.applied && hasWork(plan) && (
              <button
                type="button"
                onClick={() => apply.mutate()}
                disabled={busy}
                className="rounded-lg border border-accent px-4 py-2 text-[13px] font-semibold text-accent-2 hover:bg-accent-soft disabled:opacity-50"
              >
                {apply.isPending
                  ? 'Applying...'
                  : `Apply ${totalOf(plan, 'created') + totalOf(plan, 'updated')} changes`}
              </button>
            )}
          </footer>
        </section>
      )}

      {error && (
        <p role="alert" className="mt-5 rounded-xl border border-crit bg-crit-bg px-4 py-3 text-sm text-crit">
          {error.message}
        </p>
      )}

      {plan && <Plan plan={plan} />}

      <RegistrationLinks />
    </div>
  )
}

/**
 * Refreshes the per-class links that send a parent straight to registering for
 * the class they picked (D-112).
 *
 * **A retry, not a step.** Applying an import now refreshes these on its own
 * (D-172) — leaving it as something to remember after every upload made it a
 * recurring manual task, which is the one thing this project is designed not to
 * create. This stays for the case the import already reports: Jackrabbit's
 * listing did not answer, and the links are still stale.
 */
function RegistrationLinks() {
  const refresh = useMutation({ mutationFn: refreshRegistrationLinks })
  const result = refresh.data

  return (
    <section className="mt-8 rounded-2xl border border-line bg-surface px-5 py-4">
      <div className="flex flex-wrap items-start gap-4">
        <div className="min-w-0 flex-1">
          <h2 className="text-[14px] font-semibold">Registration links</h2>
          <p className="mt-1 max-w-[40rem] text-[12.5px] text-ink-3">
            Reads your Jackrabbit class listing so &ldquo;Register&rdquo; on the class page
            opens Jackrabbit with that class already chosen. Run it after adding or renaming
            classes. Anything it cannot match still registers through the normal form.
          </p>
        </div>

        <button
          type="button"
          onClick={() => refresh.mutate()}
          disabled={refresh.isPending}
          className="shrink-0 rounded-lg border border-line-2 px-3.5 py-2 text-[13px] font-semibold text-ink-2 hover:border-ink-3 disabled:opacity-50"
        >
          {refresh.isPending ? 'Checking Jackrabbit...' : 'Refresh links'}
        </button>
      </div>

      {refresh.isError && (
        <p role="alert" className="mt-3 rounded-xl border border-crit bg-crit-bg px-4 py-2.5 text-[13px] text-crit">
          {(refresh.error as Error).message}
        </p>
      )}

      {result && (
        <div className="mt-3 rounded-xl bg-surface-2 px-4 py-3 text-[13px]">
          <p>
            <strong>{result.matched}</strong> of {result.listed} classes linked
            {result.changed > 0 ? ` — ${result.changed} updated` : ' — nothing changed'}.
          </p>

          {result.unmatched.length > 0 && (
            /*
             * Named, not counted. These are usually harmless — a registration
             * fee is listed like a class — but a *class* appearing here means
             * its name differs between the two systems, and only a person
             * reading the name can tell those apart.
             */
            <details className="mt-2">
              <summary className="cursor-pointer text-[12.5px] font-semibold text-ink-3 hover:text-ink">
                {result.unmatched.length} in Jackrabbit that we could not match
              </summary>

              <ul className="mt-1.5 flex flex-col gap-1">
                {result.unmatched.map((name) => (
                  <li key={name} className="text-[12.5px] break-words text-ink-3 wrap-anywhere">
                    {name}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </section>
  )
}

function Plan({ plan }: { plan: ImportPlan }) {
  const missing = missingKinds(plan.files)
  const nothingToDo = !hasWork(plan)

  return (
    <div className="mt-5 flex flex-col gap-4">
      {plan.applied && (
        <div className="flex items-start gap-3 rounded-2xl border border-good bg-good-bg px-4 py-3.5">
          <CheckCircle2 size={19} strokeWidth={2} className="mt-0.5 shrink-0 text-good" />
          <div>
            <p className="text-[14px] font-semibold">Imported</p>
            <p className="mt-0.5 text-[13px] text-ink-2">
              {totalOf(plan, 'created')} created, {totalOf(plan, 'updated')} updated
              {plan.sessionsScheduled > 0 && (
                <>
                  , and {plan.sessionsScheduled.toLocaleString()} sessions put on the
                  calendar
                </>
              )}
              . The calendar is showing the new data now.
            </p>

            {/* Said only when there is something to say. Refreshing the links is
                part of applying now (D-172), so silence means it worked — a
                line confirming it every time is noise on a screen already
                carrying a lot of numbers. */}
            {plan.registrationLinks?.refreshed && plan.registrationLinks.changed > 0 && (
              <p className="mt-1 text-[12.5px] text-ink-3">
                {plan.registrationLinks.changed} registration{' '}
                {plan.registrationLinks.changed === 1 ? 'link' : 'links'} updated too.
              </p>
            )}
          </div>
        </div>
      )}

      {/* The import still succeeded. Said as its own note rather than turned
          into an error, because re-running an import that already worked is the
          one reaction that could cause harm (D-172). */}
      {plan.registrationLinks?.problem && (
        <p className="rounded-2xl border border-warn bg-warn-bg px-4 py-3 text-[13px] text-ink-2">
          {plan.registrationLinks.problem}
        </p>
      )}

      {!plan.applied && nothingToDo && (
        <div className="flex items-start gap-3 rounded-2xl border border-line bg-surface px-4 py-3.5">
          <CheckCircle2 size={19} strokeWidth={2} className="mt-0.5 shrink-0 text-good" />
          <div>
            <p className="text-[14px] font-semibold">Already up to date</p>
            <p className="mt-0.5 text-[13px] text-ink-3">
              Nothing in these files differs from what is here. {totalOf(plan, 'unchanged')} rows
              matched.
            </p>
          </div>
        </div>
      )}

      {/* ---- What was recognised ---- */}
      <section className="overflow-hidden rounded-2xl border border-line bg-surface">
        <header className="border-b border-line bg-surface-2 px-4 py-2.5">
          <h2 className="text-[13px] font-bold">Files</h2>
        </header>

        <ul className="divide-y divide-line">
          {plan.files.map((file) => (
            <li key={file.fileName} className="px-4 py-2.5">
            <div className="flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] font-semibold">{file.fileName}</p>
                {file.problem && <p className="text-[12px] text-crit">{file.problem}</p>}
              </div>

              <span
                className={`shrink-0 rounded-md px-2 py-0.5 text-[11.5px] font-semibold ${
                  file.kind === 'Unknown'
                    ? 'bg-crit-bg text-crit'
                    : 'bg-surface-2 text-ink-2'
                }`}
              >
                {FILE_KIND_LABELS[file.kind]}
              </span>

              <span className="tabular w-16 shrink-0 text-right text-[12px] text-ink-3">
                {file.rowCount} rows
              </span>
            </div>

            {/*
              Where every row went, under the file it came from.

              855 student rows becoming 735 dancers is a 120-row gap, and
              without this the screen states a confident total that quietly
              describes a different studio (D-141, D-142, D-145). The counts
              are shown beside the file so the arithmetic is checkable rather
              than asserted.
            */}
            <RowAccounting row={plan.rows.find((r) => r.kind === file.kind)} />
            </li>
          ))}
        </ul>

        {missing.length > 0 && (
          <p className="border-t border-line px-4 py-2.5 text-[12.5px] text-ink-3">
            Not included: {missing.map((k) => FILE_KIND_LABELS[k]).join(', ')}. That is fine
            &mdash; only what you send is compared.
          </p>
        )}
      </section>

      {/* ---- Counts ---- */}
      {plan.summary.length > 0 && (
        <section className="overflow-hidden rounded-2xl border border-line bg-surface">
          <header className="border-b border-line bg-surface-2 px-4 py-2.5">
            <h2 className="text-[13px] font-bold">
              {plan.applied ? 'What changed' : 'What would change'}
            </h2>
          </header>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[30rem] text-[13px]">
              <thead>
                <tr className="border-b border-line text-[11.5px] tracking-[0.06em] text-ink-3 uppercase">
                  <th className="px-4 py-2 text-left font-semibold">&nbsp;</th>
                  <th className="px-3 py-2 text-right font-semibold">New</th>
                  <th className="px-3 py-2 text-right font-semibold">Changed</th>
                  <th className="px-3 py-2 text-right font-semibold">Same</th>
                  <th className="px-4 py-2 text-right font-semibold">Skipped</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-line">
                {plan.summary.map((row) => (
                  <tr key={row.entity}>
                    <td className="px-4 py-2 font-semibold">{ENTITY_LABELS[row.entity]}</td>
                    <td className="tabular px-3 py-2 text-right">
                      {row.created > 0 ? (
                        <span className="font-semibold text-good">{row.created}</span>
                      ) : (
                        <span className="text-ink-3">&ndash;</span>
                      )}
                    </td>
                    <td className="tabular px-3 py-2 text-right">
                      {row.updated > 0 ? (
                        <span className="font-semibold text-accent-2">{row.updated}</span>
                      ) : (
                        <span className="text-ink-3">&ndash;</span>
                      )}
                    </td>
                    <td className="tabular px-3 py-2 text-right text-ink-3">{row.unchanged}</td>
                    <td className="tabular px-4 py-2 text-right text-ink-3">{row.skipped}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/*
        ---- Filed against their own names ----

        <b>Above the warnings rather than among them</b> (D-506). The import has
        warned about these since the beginning and the warning could only be
        read while an import was running, with advice to go and fix Jackrabbit;
        D-415 wrote that gap down and left it. This is the same finding with
        somewhere to act on it, and it comes before the warnings because it is the only
        thing among them that costs a family money if it is left — a class filed
        as choreography sends them to a paid private instead of a free makeup
        (D-405).

        It renders nothing when there is nothing wrong, so a clean import still
        reads as clean.

        <b>Open here, folded on the class list</b> (D-565). The fold exists
        because thirteen forms stood in front of the list somebody had opened
        to read; there is no list under it here, and this screen is the one
        D-506 called *when somebody is most likely to look*. Same component,
        and the door decides.
      */}
      <FilingConflicts defaultOpen />

      {/* ---- Anything a person has to look at ---- */}
      {(plan.needsAttention.length > 0 || plan.warnings.length > 0) && (
        <section className="overflow-hidden rounded-2xl border border-warn bg-surface">
          <header className="flex items-start gap-2.5 border-b border-line bg-warn-bg px-4 py-3">
            <AlertTriangle size={16} strokeWidth={2.5} className="mt-0.5 shrink-0 text-warn" />
            <div>
              <h2 className="text-[13.5px] font-bold">Worth a look</h2>
              <p className="mt-0.5 text-[12px] text-ink-2">
                Nothing here blocks the import. These are rows that were left alone rather
                than guessed at.
              </p>
            </div>
          </header>

          {plan.warnings.length > 0 && (
            <ul className="divide-y divide-line border-b border-line">
              {plan.warnings.map((warning, i) => (
                <li key={i} className="px-4 py-2.5 text-[13px]">
                  {warning}
                </li>
              ))}
            </ul>
          )}

          <ul className="divide-y divide-line">
            {plan.needsAttention.slice(0, 60).map((change, i) => (
              <li key={i} className="flex items-baseline gap-2.5 px-4 py-2">
                <span className="shrink-0 text-[11.5px] font-semibold text-ink-3">
                  {ENTITY_LABELS[change.entity]}
                </span>
                {/* Class labels are long machine-shaped tokens; see CalendarWeekView. */}
                <span className="min-w-0 flex-1 text-[13px] font-semibold wrap-anywhere">
                  {change.label}
                </span>
                {change.detail && (
                  <span className="min-w-0 max-w-[45%] text-right text-[12px] wrap-anywhere text-ink-3">
                    {change.detail}
                  </span>
                )}
              </li>
            ))}
          </ul>

          {plan.needsAttention.length > 60 && (
            <p className="border-t border-line px-4 py-2.5 text-[12.5px] text-ink-3">
              and {plan.needsAttention.length - 60} more
            </p>
          )}
        </section>
      )}

      {/* ---- The changes themselves ---- */}
      {plan.changes.length > 0 && (
        <section className="overflow-hidden rounded-2xl border border-line bg-surface">
          <header className="border-b border-line bg-surface-2 px-4 py-2.5">
            <h2 className="text-[13px] font-bold">Detail</h2>
          </header>

          {groupChanges(plan.changes).map((group) => (
            <details key={group.entity} className="border-b border-line last:border-b-0">
              <summary className="cursor-pointer list-none px-4 py-2.5 text-[13px] font-semibold hover:bg-surface-2">
                {ENTITY_LABELS[group.entity]}
                <span className="tabular ml-2 font-semibold text-ink-3">{group.items.length}</span>
              </summary>

              <ul className="divide-y divide-line border-t border-line">
                {group.items.map((change, i) => (
                  <li key={i} className="flex items-baseline gap-2.5 px-4 py-2">
                    <span
                      className={`w-12 shrink-0 text-[11.5px] font-semibold ${
                        change.action === 'Create' ? 'text-good' : 'text-accent-2'
                      }`}
                    >
                      {change.action === 'Create' ? 'New' : 'Changed'}
                    </span>
                    <span className="min-w-0 flex-1 text-[13px] wrap-anywhere">{change.label}</span>
                    {change.detail && (
                      <span className="min-w-0 max-w-[45%] text-right text-[12px] wrap-anywhere text-ink-3">
                        {change.detail}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </details>
          ))}
        </section>
      )}

      <p className="flex items-center gap-2 text-[12.5px] text-ink-3">
        <Trash2 size={13} strokeWidth={2} className="shrink-0" />
        The uploaded files are not kept. They are read, compared, and discarded.
      </p>
    </div>
  )
}

/**
 * Where every row in one file went.
 *
 * <b>The arithmetic is the point.</b> 855 student rows producing 735 dancers is
 * a 120-row gap, and for months the screen reported a confident total that
 * silently described a different studio — rows vanished inside `Where` clauses
 * and nobody could tell (D-141, D-142, D-145). Showing the breakdown turns
 * "trust me" into something checkable at a glance.
 *
 * Rendered only when there is something to explain: a file whose rows all
 * imported cleanly needs no commentary.
 */
function RowAccounting({ row }: { row: RowAccount | undefined }) {
  if (!row) return null

  const parts = accountParts(row)
  const gap = unaccounted(row)

  // Nothing to say when every row simply imported.
  if (gap === 0 && parts.length <= 1) return null

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px] text-ink-3">
      <span className="tabular font-semibold">{row.read} read</span>
      {parts.map((p) => (
        <span key={p.label} title={p.hint} className="tabular">
          {p.count} {p.label}
        </span>
      ))}

      {/*
        Should be impossible. It is shown rather than hidden because a silent
        discrepancy is exactly what this whole accounting exists to end — if the
        importer ever loses a row again, it says so here instead of in a support
        call six weeks later.
      */}
      {gap !== 0 && (
        <span className="tabular font-semibold text-crit">
          {gap} unaccounted &mdash; please report this
        </span>
      )}
    </div>
  )
}

/**
 * When the studio last imported (D-647).
 *
 * <b>Its own small query rather than part of the plan</b>, because it has to be
 * true before anybody uploads anything - the question is *is what I am looking
 * at current*, and an answer that only appears after an import is an answer to
 * a different question.
 *
 * <b>Says nothing rather than something wrong.</b> A failed read renders
 * nothing at all: this is a footnote, and a footnote that says "never" because
 * an endpoint refused for seventy-five seconds after a deploy (D-33) would be a
 * missing lookup rendered as a statement about the data (D-209).
 */
function LastImported() {
  const { data } = useQuery({
    queryKey: LAST_IMPORT_KEY,
    queryFn: fetchLastImport,
    staleTime: 60_000,
  })

  if (!data) return null

  return (
    <p className="mt-2 text-[12.5px] text-ink-3">
      {data.at
        ? `Last imported ${formatFullDate(data.at.slice(0, 10))}`
        : 'Nothing has been imported yet.'}
    </p>
  )
}
